import { useMemo } from "react";
import { Link } from "wouter";
import {
  ArrowUpRight,
  Activity,
  Wallet,
  Truck,
  CalendarClock,
  ClipboardList,
  FileCheck2,
  Inbox,
  PlaneLanding,
} from "lucide-react";
import { trpc } from "../lib/trpc";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { NAV_BY_ROLE } from "../components/layout/nav-config";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { MetricCard } from "../components/ui/metric-card";
import { StatusDot } from "../components/ui/status-dot";
import { useClock, fmtClockTPE } from "../lib/useClock";
import { fmtRelative } from "../lib/format";
import { OrderStatusBadge } from "../components/order/OrderStatusBadge";
import { RotatingTagline } from "../components/RotatingTagline";
import type { Language } from "../../../shared/const";

const ROLE_LABEL = {
  agency: { "zh-TW": "需求方", "en-US": "Agency", "zh-CN": "需求方" },
  fleet: { "zh-TW": "供應方", "en-US": "Fleet", "zh-CN": "供应方" },
  dispatcher: { "zh-TW": "調度中樞", "en-US": "Dispatch ops", "zh-CN": "调度中枢" },
  admin: { "zh-TW": "系統管理", "en-US": "Admin console", "zh-CN": "系统管理" },
} as const;

const ROLE_TAGLINE: Record<string, Record<Language, string>> = {
  agency: { "zh-TW": "建單、追單、看帳，一條龍。", "en-US": "Request, track, settle — end to end.", "zh-CN": "建单、追单、看帐，一条龙。" },
  fleet: { "zh-TW": "派遣即時通知，月結自動入帳。", "en-US": "Instant dispatches, automated settlements.", "zh-CN": "派遣即时通知，月结自动入账。" },
  dispatcher: { "zh-TW": "從收單到結算，全鏈路在你手上。", "en-US": "From intake to settlement, you orchestrate every step.", "zh-CN": "从收单到结算，全链路在你手上。" },
  admin: { "zh-TW": "計價、抽成、合作方、稽核 — 全局把關。", "en-US": "Pricing, take-rate, parties, audit — global oversight.", "zh-CN": "计价、抽成、合作方、稽核 — 全局把关。" },
};

/* 輪動電子字幕片語（反饋 5）：角色標語逐句輪播；reduced-motion 退回 ROLE_TAGLINE 完整句 */
const ROLE_TAGLINE_PHRASES: Record<string, Record<Language, string[]>> = {
  agency: {
    "zh-TW": ["建單", "追單", "看帳", "一條龍"],
    "en-US": ["Request", "Track", "Settle", "End to end"],
    "zh-CN": ["建单", "追单", "看帐", "一条龙"],
  },
  fleet: {
    "zh-TW": ["派遣即時通知", "一鍵確認接單", "月結自動入帳"],
    "en-US": ["Instant dispatch alerts", "One-tap confirmation", "Automated settlements"],
    "zh-CN": ["派遣即时通知", "一键确认接单", "月结自动入账"],
  },
  dispatcher: {
    "zh-TW": ["收單", "派遣", "結算", "全鏈路在你手上"],
    "en-US": ["Intake", "Dispatch", "Settle", "You orchestrate every step"],
    "zh-CN": ["收单", "派遣", "结算", "全链路在你手上"],
  },
  admin: {
    "zh-TW": ["計價", "抽成", "合作方", "稽核", "全局把關"],
    "en-US": ["Pricing", "Take-rate", "Parties", "Audit", "Global oversight"],
    "zh-CN": ["计价", "抽成", "合作方", "稽核", "全局把关"],
  },
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const now = useClock();
  const myOrdersQ = trpc.order.myList.useQuery();
  const notifQ = trpc.notification.myList.useQuery();
  const dispatcherStatsQ = trpc.order.dispatcherStats.useQuery(undefined, {
    enabled: user?.role === "dispatcher" || user?.role === "admin",
  });

  if (!user) return null;

  const { hms, date } = fmtClockTPE(now);
  const greeting = greetingFor(now);
  const tagline = ROLE_TAGLINE[user.role]!;

  // 計算 KPI（依角色）
  const orders = myOrdersQ.data ?? [];
  const stats = useMemo(() => {
    // 「進行中」= 尚未開始服務的單；active（服務中）獨立計算，兩者不重疊
    const inflight = orders.filter((o) =>
      ["pending", "quoting", "quoted", "confirmed", "dispatching"].includes(o.status),
    ).length;
    const active = orders.filter((o) => o.status === "active").length;
    const completed = orders.filter((o) => o.status === "completed").length;
    return {
      total: orders.length,
      inflight,
      active,
      completed,
      tw: orders.filter((o) => o.region === "taiwan").length,
      hk: orders.filter((o) => o.region === "hongkong").length,
    };
  }, [orders]);

  const groups = NAV_BY_ROLE[user.role];
  const quickItems = groups.flatMap((g) => g.items).filter((i) => i.to !== "/");
  // 20260617 反饋 A20：需求方（agency）不顯示「你的工作面」快捷卡 — 單一旗標同時驅動欄位版型與區塊顯示
  const showWorkspace = user.role !== "agency";
  const notifications = notifQ.data ?? [];
  const recentEvents = notifications.filter((n) => n.channel === "push").slice(0, 6);

  const dispatcherStats = dispatcherStatsQ.data;
  const fleetPending = orders.filter((o) => o.status === "dispatching" && o.fleetId).length;
  const agencyAwaitingDecision = orders.filter((o) => o.status === "quoted").length;

  // 20260611 §5：fleet 待補司機資訊（active 且未回報）；出發 < 48h 標醒目
  const missingDriverOrders = orders.filter((o) => o.status === "active" && !o.driverInfoSubmittedAt);
  const fleetMissingDriver = missingDriverOrders.length;
  const fleetMissingDriverUrgent = missingDriverOrders.some((o) => o.pickupTime - Date.now() < 48 * 3600_000);

  // KPI 卡點擊目標（客戶要求：數字 > 0 才可點，導向對應狀態的訂單清單）
  const inflightHref = "/orders?status=pending,quoting,quoted,confirmed,dispatching";

  return (
    <div className="space-y-6">
      {/* ── Operations strip ─────────────────────────────────── */}
      <header className="relative overflow-hidden rounded-xl border border-border bg-card">
        <div className="bg-grid bg-grid-fade absolute inset-0 opacity-[0.35]" />
        <div className="bg-dusk absolute inset-0" />
        <div className="bg-paper absolute inset-0" />
        <div className="relative grid grid-cols-1 gap-3 px-6 py-3.5 md:grid-cols-[1fr_auto] md:items-center md:px-7 md:py-4">
          {/* left: greeting */}
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-mono text-[12px] uppercase tracking-[0.22em] text-primary">
                {t(ROLE_LABEL[user.role])}
              </p>
              <Badge variant="outline" size="sm">
                <StatusDot tone="ok" pulse className="-ml-0.5 mr-0.5" />
                {t({ "zh-TW": "系統運行中", "en-US": "Operations live", "zh-CN": "系统运行中" })}
              </Badge>
              <Badge variant="default" size="sm">
                MVP · Phase 1
              </Badge>
            </div>
            <h1 className="font-display text-2xl font-medium leading-[1.05] tracking-tight md:text-[30px]">
              {greeting(t)}
              <span className="text-muted-foreground">，</span>
              <span className="text-primary">{user.name}</span>
              <span className="text-muted-foreground">。</span>
            </h1>
            <RotatingTagline
              phrases={ROLE_TAGLINE_PHRASES[user.role]![lang]}
              sentence={t(tagline)}
              className="max-w-xl"
            />
          </div>

          {/* right: 日期為主視覺、時間縮小為輔（反饋 4） */}
          <div className="hidden flex-col items-end text-right md:flex">
            <p className="font-mono text-[12px] uppercase tracking-[0.22em] text-muted-foreground">GMT+8 · Taipei</p>
            <p className="mt-1 font-display text-[26px] font-medium leading-none tracking-tight">{date}</p>
            <p className="numeric mt-1.5 text-sm font-light text-muted-foreground tabular-nums">{hms}</p>
          </div>
          <div className="flex items-center gap-2 md:hidden">
            <p className="font-display text-sm font-medium">{date}</p>
            <p className="numeric font-mono text-sm tabular-nums text-muted-foreground">{hms}</p>
            <span className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">GMT+8</span>
          </div>
        </div>
      </header>

      {/* ── agency 建單 CTA（第一排）：接送 / 包車 兩張大卡 ──── */}
      {user.role === "agency" && (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(
            [
              {
                href: "/orders/new?mode=transfer",
                Icon: PlaneLanding,
                title: { "zh-TW": "接送", "en-US": "Transfers", "zh-CN": "接送" },
                subtitle: { "zh-TW": "接機／送機／點對點", "en-US": "Airport pickup / drop-off / point-to-point", "zh-CN": "接机／送机／点对点" },
              },
              {
                href: "/orders/new?mode=charter",
                Icon: CalendarClock,
                title: { "zh-TW": "包車", "en-US": "Charter", "zh-CN": "包车" },
                subtitle: { "zh-TW": "時數包車", "en-US": "Hourly charter", "zh-CN": "时数包车" },
              },
            ] as const
          ).map(({ href, Icon, title, subtitle }) => (
            <Link
              key={href}
              href={href}
              className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Card className="group relative h-full cursor-pointer overflow-hidden border-primary/40 bg-gradient-to-br from-primary-soft/60 to-card transition-all hover:-translate-y-px hover:shadow-elevated">
                <div className="bg-grid bg-grid-fade absolute inset-0 opacity-20" />
                <CardContent className="relative flex items-center justify-between gap-4 p-6">
                  <div className="space-y-1.5">
                    <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-primary">
                      {t({ "zh-TW": "建立訂單", "en-US": "New order", "zh-CN": "建立订单" })}
                    </p>
                    <p className="font-display text-2xl font-medium tracking-tight">{t(title)}</p>
                    <p className="text-[12.5px] text-muted-foreground">{t(subtitle)}</p>
                  </div>
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </section>
      )}

      {/* ── KPI strip ───────────────────────────────────────── */}
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {user.role === "agency" && (
          <>
            <MetricCard
              label={t({ "zh-TW": "進行中訂單", "en-US": "Open orders", "zh-CN": "进行中订单" })}
              value={stats.inflight}
              caption={t({ "zh-TW": "尚未開始服務的單", "en-US": "not yet in service", "zh-CN": "尚未开始服务的单" })}
              tone={stats.inflight > 0 ? "primary" : "neutral"}
              Icon={ClipboardList}
              href={stats.inflight > 0 ? inflightHref : undefined}
            />
            <MetricCard
              label={t({ "zh-TW": "待你確認報價", "en-US": "Awaiting your decision", "zh-CN": "待你确认报价" })}
              value={agencyAwaitingDecision}
              caption={agencyAwaitingDecision > 0 ? t({ "zh-TW": "報價已送達，請確認或退回", "en-US": "Quotes ready for action", "zh-CN": "报价已送达，请确认或退回" }) : undefined}
              tone={agencyAwaitingDecision > 0 ? "warn" : "neutral"}
              pulse={agencyAwaitingDecision > 0}
              href={agencyAwaitingDecision > 0 ? "/orders?status=quoted" : undefined}
            />
            <MetricCard
              label={t({ "zh-TW": "服務中", "en-US": "In service", "zh-CN": "服务中" })}
              value={stats.active}
              tone={stats.active > 0 ? "primary" : "neutral"}
              Icon={Truck}
              href={stats.active > 0 ? "/orders?status=active" : undefined}
            />
            <MetricCard
              label={t({ "zh-TW": "歷史完成", "en-US": "Completed", "zh-CN": "历史完成" })}
              value={stats.completed}
              Icon={Wallet}
              href={stats.completed > 0 ? "/orders?status=completed" : undefined}
            />
          </>
        )}

        {user.role === "fleet" && (
          <>
            <MetricCard
              label={t({ "zh-TW": "待你確認派遣", "en-US": "Pending your accept", "zh-CN": "待你确认派遣" })}
              value={fleetPending}
              caption={fleetPending > 0 ? t({ "zh-TW": "請至「待確認派遣」回覆", "en-US": "Reply in dispatch inbox", "zh-CN": "请至「待确认派遣」回复" }) : undefined}
              tone={fleetPending > 0 ? "warn" : "neutral"}
              pulse={fleetPending > 0}
              href={fleetPending > 0 ? "/dispatch-inbox" : undefined}
            />
            {/* 20260611 §5：待補司機資訊（取代與「待你確認派遣」重複的「進行中接單」—
                fleet 列表僅含已派給自己的單，inflight 數恆等於待確認派遣數） */}
            <MetricCard
              label={t({ "zh-TW": "待補司機資訊", "en-US": "Missing driver info", "zh-CN": "待补司机资讯" })}
              value={fleetMissingDriver}
              caption={
                fleetMissingDriver > 0
                  ? t({ "zh-TW": "出發前 24 小時須填妥司機資訊", "en-US": "Due 24h before departure", "zh-CN": "出发前 24 小时须填妥司机资讯" })
                  : undefined
              }
              tone={fleetMissingDriverUrgent ? "danger" : fleetMissingDriver > 0 ? "warn" : "neutral"}
              pulse={fleetMissingDriverUrgent}
              href={fleetMissingDriver > 0 ? "/orders?filter=missing-driver" : undefined}
            />
            <MetricCard
              label={t({ "zh-TW": "服務中", "en-US": "In service", "zh-CN": "服务中" })}
              value={stats.active}
              tone={stats.active > 0 ? "primary" : "neutral"}
              Icon={Truck}
              href={stats.active > 0 ? "/orders?status=active" : undefined}
            />
            <MetricCard
              label={t({ "zh-TW": "歷史完成", "en-US": "Completed", "zh-CN": "历史完成" })}
              value={stats.completed}
              caption={t({ "zh-TW": "可於月結帳單核對", "en-US": "Reconcile via monthly bill", "zh-CN": "可于月结帐单核对" })}
              Icon={Wallet}
              href={stats.completed > 0 ? "/orders?status=completed" : undefined}
            />
          </>
        )}

        {(user.role === "dispatcher" || user.role === "admin") && (
          <>
            <MetricCard
              label={t({ "zh-TW": "待報價", "en-US": "Pending quote", "zh-CN": "待报价" })}
              value={dispatcherStats?.pendingQuote ?? "—"}
              tone={dispatcherStats && dispatcherStats.pendingQuote > 0 ? "warn" : "neutral"}
              pulse={!!dispatcherStats && dispatcherStats.pendingQuote > 0}
              Icon={FileCheck2}
              href={dispatcherStats && dispatcherStats.pendingQuote > 0 ? "/quoting" : undefined}
            />
            <MetricCard
              label={t({ "zh-TW": "待派遣", "en-US": "Pending dispatch", "zh-CN": "待派遣" })}
              value={dispatcherStats?.pendingDispatch ?? "—"}
              tone={dispatcherStats && dispatcherStats.pendingDispatch > 0 ? "warn" : "neutral"}
              pulse={!!dispatcherStats && dispatcherStats.pendingDispatch > 0}
              Icon={Inbox}
              href={dispatcherStats && dispatcherStats.pendingDispatch > 0 ? "/dispatch" : undefined}
            />
            <MetricCard
              label={t({ "zh-TW": "服務中", "en-US": "Active", "zh-CN": "服务中" })}
              value={dispatcherStats?.active ?? "—"}
              tone={dispatcherStats && dispatcherStats.active > 0 ? "primary" : "neutral"}
              Icon={Truck}
              href={dispatcherStats && dispatcherStats.active > 0 ? "/orders?status=active" : undefined}
            />
            <MetricCard
              label={t({ "zh-TW": "本期完成", "en-US": "Completed", "zh-CN": "本期完成" })}
              value={dispatcherStats?.completed ?? "—"}
              Icon={Activity}
              href={dispatcherStats && dispatcherStats.completed > 0 ? "/orders?status=completed" : undefined}
            />
          </>
        )}
      </section>

      {/* ── Two-column: quick actions + activity feed ───────── */}
      {/* 20260617 反饋 A20：需求方（agency）不顯示「你的工作面」快捷卡，改由側欄與建單 CTA 導覽；
          此時改單欄讓「近期活動」撐滿。fleet / dispatcher / admin 維持雙欄與原卡片清單。 */}
      <section className={`grid grid-cols-1 gap-4 ${showWorkspace ? "lg:grid-cols-[1.7fr_1fr]" : ""}`}>
        {/* Quick actions */}
        {showWorkspace && (
          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <h2 className="font-display text-[18px] font-medium tracking-tight">
                {t({ "zh-TW": "你的工作面", "en-US": "Your workspace", "zh-CN": "你的工作面" })}
              </h2>
              <p className="font-mono text-[12px] uppercase tracking-[0.16em] text-muted-foreground">
                {quickItems.length} {t({ "zh-TW": "個模組", "en-US": "modules", "zh-CN": "个模块" })}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {quickItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.to} href={item.to}>
                    <Card className="group h-full cursor-pointer transition-all hover:-translate-y-px hover:border-border-strong hover:shadow-card">
                      <div className="flex items-center gap-3 p-4">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted/50 transition-colors group-hover:border-primary/40 group-hover:bg-primary-soft">
                          <Icon className="h-4 w-4 text-foreground/70 transition-colors group-hover:text-primary" strokeWidth={1.75} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-display text-[15px] font-medium leading-tight">{t(item.label)}</p>
                          <p className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                            {item.to}
                          </p>
                        </div>
                        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground/40 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-primary" />
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Activity feed */}
        <aside className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-[18px] font-medium tracking-tight">
              {t({ "zh-TW": "近期活動", "en-US": "Activity", "zh-CN": "近期活动" })}
            </h2>
            <Link href="/notifications" className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground hover:text-primary">
              {t({ "zh-TW": "全部 →", "en-US": "All →", "zh-CN": "全部 →" })}
            </Link>
          </div>

          <Card>
            <CardContent className="p-0">
              {recentEvents.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="font-display text-base text-muted-foreground">
                    {t({ "zh-TW": "尚無活動", "en-US": "No activity yet", "zh-CN": "尚无活动" })}
                  </p>
                  <p className="mt-1 text-[12px] text-muted-foreground/80">
                    {t({
                      "zh-TW": "建單、報價、派遣等事件會即時顯示在這裡。",
                      "en-US": "Order, quote and dispatch events will surface here.",
                      "zh-CN": "建单、报价、派遣等事件会即时显示在这里。",
                    })}
                  </p>
                </div>
              ) : (
                <ol className="divide-y divide-border">
                  {recentEvents.map((n) => {
                    const url = (n.payload as { url?: string } | null)?.url;
                    const path = url?.replace(/^https?:\/\/[^/]+/, "") ?? "/notifications";
                    return (
                      <li key={n.id}>
                        <Link href={path}>
                          <a className="group flex items-start gap-3 p-3.5 transition-colors hover:bg-muted/40">
                            <StatusDot
                              tone={
                                n.eventType.includes("rejected") || n.eventType.includes("cancelled")
                                  ? "danger"
                                  : n.eventType.includes("completed") || n.eventType.includes("accepted")
                                  ? "ok"
                                  : "warn"
                              }
                              className="mt-2"
                            />
                            <div className="min-w-0 flex-1 space-y-0.5">
                              <p className="truncate text-[13px] font-medium text-foreground/90">{n.title}</p>
                              <p className="line-clamp-2 text-[12px] text-muted-foreground">{n.body}</p>
                              <p className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground/70">
                                {fmtRelative(n.createdAt)}
                              </p>
                            </div>
                          </a>
                        </Link>
                      </li>
                    );
                  })}
                </ol>
              )}
            </CardContent>
          </Card>
        </aside>
      </section>
    </div>
  );
}

function greetingFor(now: Date): (t: ReturnType<typeof useLanguage>["t"]) => string {
  const h = Number(new Intl.DateTimeFormat("en-US", { hour: "numeric", hour12: false, timeZone: "Asia/Taipei" }).format(now));
  if (h < 5) return (t) => t({ "zh-TW": "深夜辛苦了", "en-US": "Working late", "zh-CN": "深夜辛苦了" });
  if (h < 12) return (t) => t({ "zh-TW": "早安", "en-US": "Good morning", "zh-CN": "早安" });
  if (h < 18) return (t) => t({ "zh-TW": "午安", "en-US": "Good afternoon", "zh-CN": "午安" });
  return (t) => t({ "zh-TW": "晚安", "en-US": "Good evening", "zh-CN": "晚安" });
}
